import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container } from 'reactstrap';
import { FiDownload, FiRefreshCw, FiClock } from 'react-icons/fi';
import { FaWindows, FaApple, FaLinux } from 'react-icons/fa';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Input } from '@components/Input';
import { Table } from '@components/Table';
import { Modal } from '@components/Modal';
import { IconBadge, iconBadgeSizes } from '@components/IconBadge';
import { InitialsBadge } from '@components/InitialsBadge';
import { addNotification, notificationStates } from '@components/Notification';
import appVersionService from '@services/appVersion';

import styles from './AppVersionPublish.module.scss';

// Keep in sync with STAFF_UIDS in functions/staff-auth.js.
// There's no "Prodaff staff" identity concept elsewhere in this codebase; this
// is a standalone, hardcoded gate for this one internal-only page.
const STAFF_UIDS = [
  'tBSIKtrev2UYKWz3bgk4VQlbWie2', // ayush@prodaff.com
  'aMep0ZI5tBcyDYlJI6tb1OqxLBw2', // ayush@wfhgo.com
];

// Installer download links — not arch-specific, so a flat list of the three
// OSes (windows/macos/linux), same icons the Download Desktop App page uses.
const DOWNLOAD_PLATFORM_META = {
  windows: { label: 'Windows', icon: FaWindows, color: '#0078d4' },
  macos: { label: 'macOS', icon: FaApple, color: '#1a1a1a' },
  linux: { label: 'Linux', icon: FaLinux, color: '#1a1a1a' },
};

const formatTimestamp = (ts) => {
  const seconds = ts?._seconds ?? ts?.seconds;
  if (seconds == null) return null;
  return new Date(seconds * 1000);
};

export const AppVersionPublishComponent = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const isAuthorized = STAFF_UIDS.includes(userProfile?.id);

  const [downloadPlatforms, setDownloadPlatforms] = useState([]);
  const [downloadDrafts, setDownloadDrafts] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [savingDownloadPlatform, setSavingDownloadPlatform] = useState(null);
  const [downloadHistoryPlatformKey, setDownloadHistoryPlatformKey] =
    useState(null);

  const loadDownloadConfig = useCallback(async () => {
    try {
      const res = await appVersionService.getAppDownloadConfig();
      if (res.success) {
        setDownloadPlatforms(res.data.platforms);
        setDownloadDrafts(
          res.data.platforms.reduce((acc, p) => {
            acc[p.platform] = {
              downloadUrl: p.downloadUrl || '',
              currentVersion: p.currentVersion || '',
            };
            return acc;
          }, {})
        );
      } else {
        addNotification({
          message: res.message || 'Failed to load download links.',
          level: notificationStates.error,
        });
      }
    } catch (err) {
      addNotification({
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to load download links.',
        level: notificationStates.error,
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthorized) loadDownloadConfig();
  }, [isAuthorized, loadDownloadConfig]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDownloadConfig();
    setRefreshing(false);
  };

  const handleSaveDownloadUrl = async (platform) => {
    const draft = downloadDrafts[platform] || {};
    const downloadUrl = (draft.downloadUrl || '').trim();
    const currentVersion = (draft.currentVersion || '').trim();
    if (!downloadUrl || !currentVersion) {
      addNotification({
        message: 'Download URL and version are both required.',
        level: notificationStates.error,
      });
      return;
    }
    setSavingDownloadPlatform(platform);
    try {
      const res = await appVersionService.publishAppDownloadUrl({
        platform,
        downloadUrl,
        currentVersion,
      });
      if (res.success) {
        addNotification({
          message: `Updated ${DOWNLOAD_PLATFORM_META[platform]?.label || platform} to v${currentVersion}.`,
          level: notificationStates.success,
        });
        loadDownloadConfig();
      } else {
        addNotification({
          message: res.message || 'Failed to update download link.',
          level: notificationStates.error,
        });
      }
    } catch (err) {
      addNotification({
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to update download link.',
        level: notificationStates.error,
      });
    } finally {
      setSavingDownloadPlatform(null);
    }
  };

  const downloadColumns = [
    {
      id: 'platform',
      name: 'Platform',
      cellRenderer: (row) => {
        const meta = DOWNLOAD_PLATFORM_META[row.platform];
        const Icon = meta?.icon;
        return (
          <div className={styles.platformCell}>
            {Icon && <Icon size={18} color={meta.color} />}
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Medium}
              color="gray-900"
            >
              {meta?.label || row.platform}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'downloadUrl',
      name: 'Download URL',
      cellRenderer: (row) => (
        <Input
          id={`downloadUrl-${row.platform}`}
          placeholder="https://..."
          value={downloadDrafts[row.platform]?.downloadUrl ?? ''}
          onChange={(e) =>
            setDownloadDrafts((prev) => ({
              ...prev,
              [row.platform]: {
                ...prev[row.platform],
                downloadUrl: e.target.value,
              },
            }))
          }
        />
      ),
    },
    {
      id: 'currentVersion',
      name: 'Version',
      cellRenderer: (row) => (
        <div className={styles.downloadUrlCell}>
          <Input
            id={`downloadVersion-${row.platform}`}
            placeholder="e.g. 3.7.0.27w"
            value={downloadDrafts[row.platform]?.currentVersion ?? ''}
            onChange={(e) =>
              setDownloadDrafts((prev) => ({
                ...prev,
                [row.platform]: {
                  ...prev[row.platform],
                  currentVersion: e.target.value,
                },
              }))
            }
          />
          <Button
            text={savingDownloadPlatform === row.platform ? 'Saving…' : 'Save'}
            variant={buttonVariants.primary_outline}
            onClick={() => handleSaveDownloadUrl(row.platform)}
            disabled={
              savingDownloadPlatform === row.platform ||
              ((downloadDrafts[row.platform]?.downloadUrl || '') ===
                (row.downloadUrl || '') &&
                (downloadDrafts[row.platform]?.currentVersion || '') ===
                  (row.currentVersion || ''))
            }
          />
        </div>
      ),
    },
    {
      id: 'updatedAt',
      name: 'Last Updated',
      cellRenderer: (row) => {
        const date = formatTimestamp(row.updatedAt);
        if (!date)
          return (
            <Text variant={textVariantsEnum.Text_sm} color="gray-500">
              —
            </Text>
          );
        return (
          <div className={styles.dateCell}>
            <Text variant={textVariantsEnum.Text_sm} color="gray-900">
              {date.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
            <Text variant={textVariantsEnum.Text_13} color="gray-500">
              {date.toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'updatedBy',
      name: 'Updated By',
      cellRenderer: (row) =>
        row.updatedBy ? (
          <div className={styles.updatedByCell}>
            <InitialsBadge label={row.updatedBy} />
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Medium}
              color="gray-900"
            >
              {row.updatedBy}
            </Text>
          </div>
        ) : (
          <Text variant={textVariantsEnum.Text_sm} color="gray-500">
            —
          </Text>
        ),
    },
    {
      id: 'actions',
      name: '',
      style: { width: 56 },
      cellRenderer: (row) => (
        <button
          type="button"
          className={styles.actionsTrigger}
          aria-label={`View history for ${DOWNLOAD_PLATFORM_META[row.platform]?.label || row.platform}`}
          onClick={() => setDownloadHistoryPlatformKey(row.platform)}
        >
          <FiClock size={16} />
        </button>
      ),
    },
  ];

  const downloadHistoryPlatform = downloadPlatforms.find(
    (p) => p.platform === downloadHistoryPlatformKey
  );

  // Gate applied last, after every hook above has run unconditionally on
  // every render (rules-of-hooks) — the actual data-access gate is server
  // side (functions/app-download-api.js); this just keeps the page from
  // rendering its shell for anyone else.
  if (!isAuthorized) {
    return null;
  }

  return (
    <Container className={styles.page}>
      {/* No local title here — the route's `name`/`subtitle` (routesData.jsx)
          already render this page's title in the shared Navbar; repeating
          it here would show "App Versions" twice on screen. */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <IconBadge
              icon={<FiDownload size={20} color="var(--indigo-600)" />}
              bg="indigo-100"
              size={iconBadgeSizes.lg}
            />
            <div>
              <Text
                variant={textVariantsEnum.Text_lg}
                fontWeight={fontWeightEnum.Semibold}
                color="gray-900"
              >
                Desktop App Download Links
              </Text>
              <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                Full installer links + version shown on the Download Desktop App
                page, onboarding, and given to partners for new installs.
              </Text>
            </div>
          </div>
          <Button
            text={refreshing ? 'Refreshing…' : 'Refresh'}
            variant={buttonVariants.primary_outline}
            icon={<FiRefreshCw size={16} />}
            onClick={handleRefresh}
            disabled={refreshing}
          />
        </div>

        <Table columns={downloadColumns} data={downloadPlatforms} />
      </div>

      <Modal
        isVisible={!!downloadHistoryPlatformKey}
        onClose={() => setDownloadHistoryPlatformKey(null)}
      >
        <div className={styles.historyModal}>
          <Text
            variant={textVariantsEnum.Text_lg}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            {DOWNLOAD_PLATFORM_META[downloadHistoryPlatformKey]?.label ||
              downloadHistoryPlatformKey}{' '}
            history
          </Text>
          {!downloadHistoryPlatform?.history?.length && (
            <Text variant={textVariantsEnum.Text_sm} color="gray-500">
              No history yet.
            </Text>
          )}
          {downloadHistoryPlatform?.history?.map((entry) => (
            <div key={entry.id} className={styles.historyRow}>
              <Text
                variant={textVariantsEnum.Text_sm}
                fontWeight={fontWeightEnum.Medium}
                color="gray-900"
              >
                v{entry.fromVersion || 'none'} → v{entry.toVersion}
              </Text>
              <Text
                variant={textVariantsEnum.Text_sm}
                color="gray-500"
                className={styles.historyUrlText}
              >
                {entry.fromUrl || 'none'} → {entry.toUrl}
              </Text>
              <Text variant={textVariantsEnum.Text_13} color="gray-500">
                {formatTimestamp(entry.changedAt)?.toLocaleString() || '—'} ·{' '}
                {entry.changedBy}
              </Text>
            </div>
          ))}
        </div>
      </Modal>
    </Container>
  );
};
